/**
 * @file V1 fallback for the Separation Health Assessment Upload page.
 *
 * Uses the legacy FileField upload component instead of `va-file-input-multiple`.
 * Shown when the evidence enhancement toggle is ON but the FileInputV3 toggle
 * is OFF. Once FileInputV3 is fully rolled out, this page can be removed.
 */

import React, { useEffect } from 'react';
import fileUploadUI from 'platform/forms-system/src/js/definitions/file';
import environment from '@department-of-veterans-affairs/platform-utilities/environment';

import { isUploadingBddSha } from '../utils';
import {
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_BYTES,
  MAX_PDF_FILE_SIZE_BYTES,
} from '../../shared/constants';
import { additionalInfo } from '../../shared/components/fileInputComponent/AdditionalUploadInfo';
import { trackShaPageSeen } from '../utils/tracking/bddShaTracking';
import {
  createUploadPayload,
  createParseResponse,
} from '../utils/uploadHelpers';
import UploadValidationWarning from '../components/UploadValidationWarning';

const MAXIMUM_NUMBER_OF_FILES = 20;
const SHA_ATTACHMENT_ID = 'L1839';
const SHA_WARNING_TITLE = <h3>Make sure you upload the correct file</h3>;

const FILE_UPLOAD_DESCRIPTION = (
  <>
    <p className="vads-u-color--gray-medium vads-u-margin-top--0">
      You can upload .pdf, .jpg, .jpeg, or .png files. Each file should be no
      larger than {MAX_FILE_SIZE_MB} MB. Larger files may take longer to upload,
      depending on the internet connection. You can upload a maximum of{' '}
      {MAXIMUM_NUMBER_OF_FILES} files.
    </p>
    <div className="vads-u-margin-y--2">
      <UploadValidationWarning
        formDataPath="separationHealthAssessmentUploads"
        warningTitle={SHA_WARNING_TITLE}
        scrollOffset={10}
      >
        <p>
          You may have uploaded a file that’s not the Separation Health
          Assessment (self-assessment, also called “Part A”).{' '}
        </p>
        <p>
          <va-link
            external
            href="https://www.benefits.va.gov/compensation/docs/SHA_DBQ_Part_A_Self-Assessment.pdf"
            text="Download the Separation Health Assessment PDF"
          />
        </p>
        <p>
          <strong>Note: </strong>
          You can continue completing this claim without uploading a new file.
          But you may not qualify for the Benefits Delivery at Discharge (BDD)
          Program.
        </p>
      </UploadValidationWarning>
    </div>
  </>
);

const createPayload = createUploadPayload({ attachmentId: SHA_ATTACHMENT_ID });

const parseResponse = createParseResponse();

const fileUploadUi = fileUploadUI('Select a file to upload', {
  itemDescription: '',
  hideLabelText: false,
  fileUploadUrl: `${environment.API_URL}/v0/upload_supporting_evidence`,
  buttonText: 'Upload file',
  addAnotherLabel: 'Add another file',
  fileTypes: ['pdf', 'jpg', 'jpeg', 'png'],
  maxSize: MAX_FILE_SIZE_BYTES,
  maxPdfSize: MAX_PDF_FILE_SIZE_BYTES,
  minSize: 1,
  createPayload,
  parseResponse,
});

/**
 * Tracks that the SHA upload page has been seen.
 * Rendered as part of ui:description so it fires on page mount.
 */
const ShaUploadDescription = () => {
  useEffect(() => {
    trackShaPageSeen('upload');
  }, []);

  return (
    <div>
      <p>
        Upload your Separation Health Assessment (self-assessment, also called
        “Part A”) to support your claim.
      </p>
      <div className="vads-u-margin-top--2 vads-u-margin-bottom--2">
        {additionalInfo}
      </div>
    </div>
  );
};

export const uiSchema = {
  'ui:title': (
    <h3 className="vads-u-margin-y--0 vads-u-color--base">
      Upload your Separation Health Assessment
    </h3>
  ),
  'ui:description': <ShaUploadDescription />,
  separationHealthAssessmentUploads: {
    ...fileUploadUi,
    'ui:options': {
      ...fileUploadUi['ui:options'],
      fileTypes: ['pdf', 'jpg', 'jpeg', 'png'],
    },
    // Utilizing custom JSX instead of FileUploadDescriptions component because file type and max file size +
    // requirements are specific to supporting future upload validation efforts and this V1 version of upload is a
    // temporary holdover.
    'ui:description': FILE_UPLOAD_DESCRIPTION,
    'ui:confirmationField': ({ formData }) => ({
      data: formData?.map(item => item.name || item.fileName),
      label: 'Uploaded file(s)',
    }),
    'ui:required': data => isUploadingBddSha(data),
  },
};

export const schema = {
  type: 'object',
  properties: {
    separationHealthAssessmentUploads: {
      type: 'array',
      items: {
        type: 'object',
        required: ['name'],
        properties: {
          name: {
            type: 'string',
          },
          confirmationCode: {
            type: 'string',
          },
        },
      },
      minItems: 1,
      maxItems: MAXIMUM_NUMBER_OF_FILES,
    },
  },
};
